import { useMemo } from "react";
import {
  VStack,
  Select,
  Button,
  Text,
  Textarea,
  Alert,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";

import type { FormStepProps } from "../interfaces";
import { INPUT_SPACING, INPUT_VARIANT, INPUT_SIZE } from "../RegisterPage";
import FormInputWraper from "../Components/FormInputWrapper";
import { FormWrapper } from "../Components/LayoutComponents";
import RegisterHeading from "../Components/RegisterHeading";
import { useCoachesListQuery } from "../../../lib/api/sdk";

const StepThree = ({
  onSubmit,
  skipStep: _skipStep,
  RegisterMutationResult,
  FormController,
  goBack,
}: FormStepProps) => {
  const t = useTranslations("pages.register");
  const CoachesListReq = useCoachesListQuery();
  const coach_id = FormController.watch("coach_id");

  const coachesWithValidPlans = useMemo(() => {
    const selectedSportId = FormController.watch("sport_id");
    return CoachesListReq.data?.coach.filter(coach => {
      const coachPlans = coach.sports.find(cs => cs.sport.id === selectedSportId)?.sport
        .subscription_plans?.filter(plan => plan.is_verified && plan.coach_id === coach.id) || [];
      return coachPlans.length > 0;
    });
  }, [CoachesListReq.data?.coach]);

  return (
    <FormWrapper>
      <RegisterHeading
        color="brandOrange.500"
        fontSize="4xl"
        mb="8"
      >
        {t("goalsAndCoach")}
      </RegisterHeading>
      <VStack spacing={INPUT_SPACING} w="full">
        {CoachesListReq.error && (
          // eslint-disable-next-line
          <Alert status="error">
            {t("cantLoadCoaches")}{" "}
            {CoachesListReq.error.message}.{" "}
            <Button onClick={() => CoachesListReq.refetch()} variant={"link"}>
              {t("tryAgain")}
            </Button>
          </Alert>
        )}
        <FormInputWraper
          isRequired={false}
          label={t("goal")}
          field_name="goal"
          color="brandOrange.500"
          FormController={FormController}
        >
          <Textarea
            variant={INPUT_VARIANT}
            size={INPUT_SIZE}
            {...FormController.register("goal")}
          />
        </FormInputWraper>

        <FormInputWraper
          isRequired={false}
          label={t("coach")}
          field_name="coach_id"
          color="brandOrange.500"
          FormController={FormController}
        >
          <Select
            variant={INPUT_VARIANT}
            size={INPUT_SIZE}
            {...FormController.register("coach_id")}
            onChange={e => {
              if (coach_id === e.target.value) return;
              FormController.setValue("coach_id", e.target.value);
              FormController.setValue("plan_id", "");
            }}
          >
            <option value={""}>{t("noPreference")}</option>
            {coachesWithValidPlans?.map(coach => {
              return (
                <option
                  key={coach.id}
                  value={coach.id}
                  selected={coach_id === coach.id}
                >
                  {coach.name}
                </option>
              );
            })}
          </Select>
        </FormInputWraper>
      </VStack>

      <VStack spacing={5} align="start" w="full" mt="6">
        {RegisterMutationResult?.error && (
          <Text color="red.500">{RegisterMutationResult?.error.message}</Text>
        )}
        <Button
          colorScheme="brandOrange"
          onClick={FormController.handleSubmit(onSubmit)}
          isLoading={RegisterMutationResult?.loading}
        >
          {t("nextAthleteButton3")}
        </Button>

        <Button colorScheme="gray" variant="link" onClick={goBack}>
          {t("backAthleteButton2")}
        </Button>
      </VStack>
    </FormWrapper >
  );
};

export default StepThree;
